/**
 * Client-side ("in-page") row sorting for DataTables that use server pagination.
 *
 * These lists keep `lazy` server pagination (one page at a time), but sorting is
 * applied in the browser to the currently-loaded rows — no extra backend call.
 * Pair with a DataTable that has `lazy`, `sortField`/`sortOrder` state, and an
 * `onSort` handler that just stores the field/order (see any RBAC/logs tab).
 */

/** Read a possibly-nested field (e.g. "district.name") off a row. */
const readPath = (row: any, path: string): unknown => {
  if (!path) return undefined;
  if (path.indexOf('.') === -1) return row?.[path];
  return path.split('.').reduce((acc: any, k) => (acc == null ? acc : acc[k]), row);
};

/**
 * Return a new array sorted by `field`/`order` (1 asc, -1 desc). When field/order
 * are null the original array is returned unchanged. Nullish values sort last;
 * numbers/dates compare numerically, everything else compares as lowercased text.
 */
export function sortRows<T>(rows: T[], field: string | null, order: 1 | -1 | null): T[] {
  if (!field || !order || !Array.isArray(rows)) return rows;
  const dir = order;
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = readPath(a, field);
    const bv = readPath(b, field);
    // Nullish always sorts to the bottom regardless of direction.
    const aNull = av === null || av === undefined || av === '';
    const bNull = bv === null || bv === undefined || bv === '';
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;

    const ad = av instanceof Date ? av.getTime() : Date.parse(String(av));
    const bd = bv instanceof Date ? bv.getTime() : Date.parse(String(bv));
    if (!Number.isNaN(ad) && !Number.isNaN(bd) && /\d{4}-\d{2}-\d{2}/.test(String(av))) {
      return (ad - bd) * dir;
    }

    return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * dir;
  });
  return copy;
}
